/**
 * Refresh policy — authoritative table for "when does each tier
 * re-fetch". Read this file AND `docs/REFRESH-POLICY.md` before
 * touching any refresh-related code. The matrix here must stay
 * aligned with the doc; if they diverge, the doc wins and this code
 * needs to catch up.
 *
 * Tiers split data by freshness requirement — see the doc for full
 * taxonomy. The short version:
 *   • T1_LIVE       — hero $ number, cash, buying power, today's %.
 *   • T2_METRICS    — window metrics (weekly / YTD / all-time).
 *   • T3_LISTS      — orders, activities, bots, positions.
 *   • T4_REFERENCE  — historical quotes, SPY, splits.
 *
 * The policy is a pure function of four inputs:
 *   (tier, visibility, marketState, engagement) → { intervalMs, enabled }
 *
 * `intervalMs === null` means "no auto-poll — refreshes are event-
 * driven only (focus / online / user-action / manual)". `enabled`
 * folds in whether the current conditions allow the event-driven
 * paths too.
 */
import type { MarketState } from "@/lib/market-status";

export type RefreshTier = "T1_LIVE" | "T2_METRICS" | "T3_LISTS" | "T4_REFERENCE";
export type TabVisibility = "visible" | "hidden";
export type UserEngagement = "active" | "soft-idle" | "hard-idle";

export interface PolicyInputs {
  tier: RefreshTier;
  visibility: TabVisibility;
  marketState: MarketState;
  engagement: UserEngagement;
}

export interface PolicyOutput {
  /**
   * Auto-poll interval in ms while the current conditions hold. `null`
   * means "no interval" — caller relies on focus / online / user-action
   * triggers only.
   */
  intervalMs: number | null;
  /**
   * Whether any fetch is allowed at all right now. `false` stops
   * interval timers AND event-driven fetches. Used to freeze hidden
   * tabs and `hard-idle` states.
   */
  enabled: boolean;
  /**
   * Whether the tier should participate in focus / online / market-open
   * triggers even when `intervalMs === null`. Keeps event-driven
   * refreshes firing while auto-poll is paused (e.g. market-closed
   * states).
   */
  eventDriven: boolean;
}

/** Minute in ms — helper so intervals read as human units. */
const MIN = 60_000;

/** Canonical T1 poll interval while active + focused + market open. */
const T1_POLL_MS = 15 * MIN;

/**
 * Resolve the refresh policy for a given set of inputs.
 *
 * Rules implemented here mirror the table in `docs/REFRESH-POLICY.md`:
 *
 *   • Hidden tabs disable everything for every tier.
 *   • Hard-idle disables auto-refresh for every tier; modal drives.
 *   • Soft-idle lets T1 run once more at the 15 min mark, then pauses.
 *     (Represented as `intervalMs: null + eventDriven: true` — the
 *     caller's scheduler checks engagement on each tick.)
 *   • Market-closed downgrades all tiers to event-driven (on-focus,
 *     on-online) only.
 *   • T4 never auto-polls; immutable reference data loads on demand.
 */
export function resolveRefreshPolicy(inputs: PolicyInputs): PolicyOutput {
  const { tier, visibility, marketState, engagement } = inputs;

  // Hidden tabs: everything off. No interval, no event-driven fetch
  // either — we let the `visibilitychange` listener fire on return and
  // re-resolve from scratch.
  if (visibility === "hidden") {
    return { intervalMs: null, enabled: false, eventDriven: false };
  }

  // Hard-idle: no auto-refresh. Modal-driven only. Event-driven stays
  // off too because we don't want a focus-regained event (which
  // happens every time the user mouses over) to quietly refetch while
  // the idle modal is visible. The manual "Refresh" button inside the
  // modal bypasses the policy via the `manual` trigger path.
  if (engagement === "hard-idle") {
    return { intervalMs: null, enabled: false, eventDriven: false };
  }

  // T4 — historical / reference data. Never auto-polls; caller uses
  // the IDB cache and fetches on cache miss only.
  if (tier === "T4_REFERENCE") {
    return { intervalMs: null, enabled: true, eventDriven: true };
  }

  // Market closed: everything downgrades to event-driven (focus /
  // online / manual). No interval for any tier since prices don't
  // move intraday.
  if (marketState === "closed") {
    return { intervalMs: null, enabled: true, eventDriven: true };
  }

  // Market open.
  // T1 auto-polls while active; pauses while soft-idle (the caller's
  // scheduler is expected to fire one more T1 at the 15-min mark
  // before downshifting — that logic lives in the coordinator, not
  // here).
  if (tier === "T1_LIVE") {
    if (engagement === "active") {
      return { intervalMs: T1_POLL_MS, enabled: true, eventDriven: true };
    }
    // soft-idle: no new interval starts, but event-driven fetches (a
    // returning focus after a background) still fire.
    return { intervalMs: null, enabled: true, eventDriven: true };
  }

  // T2 and T3: market-open doesn't trigger interval polling — only
  // events (focus / online / user-action). Same as market-closed but
  // kept as an explicit branch so the distinction reads clearly in
  // reviews.
  return { intervalMs: null, enabled: true, eventDriven: true };
}

/** Thresholds for `UserEngagement`. Centralised so tests and the
 *  idle-watchdog UI read the same numbers. */
export const ENGAGEMENT_THRESHOLDS = {
  /** Below this → `"active"`. */
  softIdleMs: 15 * MIN,
  /** At or above this → `"hard-idle"`. */
  hardIdleMs: 30 * MIN,
  /** Sleep-wake watchdog: if the Date.now() delta between ticks
   *  exceeds this, we assume the machine slept and immediately enter
   *  hard-idle regardless of interaction timestamps. */
  sleepDetectionMs: 2 * MIN
} as const;

/** Derive the engagement state from a last-interaction timestamp.
 *  Extracted for reuse by both the hook and any test scaffolding. */
export function engagementFor(
  lastInteractionAt: number,
  now: number
): UserEngagement {
  const age = now - lastInteractionAt;
  if (age >= ENGAGEMENT_THRESHOLDS.hardIdleMs) return "hard-idle";
  if (age >= ENGAGEMENT_THRESHOLDS.softIdleMs) return "soft-idle";
  return "active";
}
